import { Box, Chip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RangeSlider } from 'ui';
import { theme } from 'theme';

type RangeSliderExtendedProps = {
  onChange: (newValues: any) => void;
  label: string;
  values: RangeFilter | (string | number)[];
  options: {
    label: string;
    value: any;
  }[];
};

type RangeFilter = {
  fromRange?: string | number | null;
  toRange?: string | number | null;
  emptyValues?: boolean;
};

const useStyles = makeStyles(() => ({
  chipStyles: {
    width: '49%',
    fontWeight: 500,
    height: theme.spacing(3),
  },
  noRangeLine: {
    width: '100%',
    height: '2px',
    display: 'block',
    opacity: 0.38,
    borderRadius: '1px',
    backgroundColor: 'currentColor',
  },
  noRangeLineWrapper: {
    padding: '13px 0px',
    height: 53.02,
  },
}));

export const RangeSliderExtended: FC<RangeSliderExtendedProps> = ({
  onChange,
  label,
  options,
  values,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { fromRange, toRange, emptyValues } = values as RangeFilter;

  const [rangeActive, setRangeActive] = useState<boolean>(
    (!!fromRange || !!toRange || !emptyValues) ?? true,
  );

  const [noValueActive, setNoValueActive] = useState<boolean>(
    ((values as RangeFilter).emptyValues as boolean) ?? true,
  );

  // eslint-disable-next-line consistent-return
  const rangeSliderValueParser = (data: any) => {
    if (data.fromRange !== null && data.fromRange !== undefined) {
      if (fromRange !== undefined && toRange !== undefined) {
        return [fromRange, toRange];
      }
      if (!fromRange) {
        return [options[0].value, options[options.length - 1].value];
      }
    }
    if (!data.fromRange) {
      return [options[0].value, options[options.length - 1].value];
    }
    return data as (string | number)[];
  };

  const valuesParsed = rangeSliderValueParser(values);
  const handleChange = (_event: EventTarget, newValues: any) =>
    onChange({
      fromRange: newValues[0],
      toRange: newValues[1],
      emptyValues: noValueActive,
    });

  useEffect(() => {
    if (values && (values as string[]).length) {
      setNoValueActive(true);
      setRangeActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    if (!rangeActive && !noValueActive) {
      chipButtonHandler('rangeActive', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeActive, noValueActive]);

  const chipButtonHandler = (variant: 'rangeActive' | 'noValueActive', value: boolean) => {
    let isRangeActive: boolean = rangeActive;
    let isNoValueActive: boolean = noValueActive;
    if (variant === 'rangeActive') {
      isRangeActive = value;
      setRangeActive(value);
    }
    if (variant === 'noValueActive') {
      isNoValueActive = value;
      setNoValueActive(value);
    }

    onChange(
      isRangeActive
        ? ({
            fromRange: fromRange ?? valuesParsed[0],
            toRange: toRange ?? valuesParsed[1],
            emptyValues: isNoValueActive,
          } as RangeFilter)
        : ({
            emptyValues: isNoValueActive,
          } as RangeFilter),
    );
  };

  return (
    <Box mt={2}>
      <Typography gutterBottom>{label}</Typography>
      <Box display="flex" justifyContent="space-between" width="80" mb={2}>
        <Chip
          label={t('RANGE')}
          className={classes.chipStyles}
          clickable
          color={rangeActive ? 'primary' : undefined}
          onClick={() => chipButtonHandler('rangeActive', !rangeActive)}
        />
        <Chip
          label={t('NO_VALUE')}
          className={classes.chipStyles}
          clickable
          color={noValueActive ? 'primary' : undefined}
          onClick={() => chipButtonHandler('noValueActive', !noValueActive)}
        />
      </Box>
      {!rangeActive && noValueActive ? (
        <div className={classes.noRangeLineWrapper}>
          <div className={classes.noRangeLine} />
        </div>
      ) : (
        <div>
          <RangeSlider
            alignSliderToLabel
            options={options}
            values={valuesParsed}
            onChange={handleChange}
          />
        </div>
      )}
    </Box>
  );
};
